import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { getDayOfWeek } from 'commons/utils/DateUtils';

/**
 * MUI
 */
import {
  Grid,
  makeStyles,
  withStyles,
  Paper,
  Theme,
  Button,
  Typography,
  Divider,
  Tooltip,
  IconButton,
  Hidden,
} from '@material-ui/core';
import SwitchVideoIcon from '@material-ui/icons/SwitchVideo';

/**
 * Commons
 */
import { File, Resolution } from 'commons/constants/KeyFlowConstants';
import CategoryVO from 'commons/vo/CategoryVO';
import { useAsset, useLibrary } from 'hooks';
import StorageVO from 'commons/vo/StorageVO';
import { RootState } from 'redux/reducers/type';
import CodeVO from 'commons/vo/CodeVO';

import TimeCode from 'commons/utils/TimeCode';
import LibraryParamVO from 'commons/vo/param/LibraryParamVO';

import { VersusIcon } from 'asset/images';
import DownloadAssetSplitButton from 'components/DownloadAssetSplitButton';

/**
 * Components
 */
import { DownloadButton, MobileDownloadButton } from 'components/Button/DownloadButton';
import { AssetProps } from 'components/AssetThumbnailView/type';

const useStyles = makeStyles((theme: Theme) => ({
  assetItem: {
    // 기존의 계산식 85px - 16px(spacing 2) = 69px
    // 기존의 계산식이 어떤걸 의미하는지는 모르겠으나 변경해도 문제 없는것 같아 요청사항 대로 변경
    marginBottom: '35px',
    [theme.breakpoints.down('md')]: {
      marginBottom: '35px',
    },
  },
  paper: {
    height: '307px',
    boxShadow: theme.shadows[0],
    [theme.breakpoints.down('md')]: {
      height: '232px',
    },
  },
  imgContainer: {
    // [Skim 의 스크러빙 로직 때문에 높이를 지정하면 안됨]
    // - skim 이미지 비율 16:9 이미지가 50장이 세로로 이어 붙임
    // - 때문에 width의 56.25% 의 height 를 가져가야 함
    position: 'relative',
    backgroundOrigin: 'content-box',
    backgroundSize: 'cover',
    paddingTop: '56.25%',
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      // 모바일에서 skim 이 동작하지 않는다면 영역 크기 문제로 height 를 지정해주거나 최대 너비등을 지정해야함.
      minWidth: '209px',
      minHeight: '185px',
      paddingTop: 0,
      backgroundPosition: 'center !important',
    },
  },
  img: {
    width: '100%',
  },
  scrubbingBar: {
    position: 'absolute',
    top: 0,
    width: 0,
    height: '100%',
    border: '0.8px solid rgba(255, 30, 1, 0.8)',
  },
  assetContainer: {
    height: '62px',
    display: 'flex',
    marginTop: '10px',
    justifyContent: 'space-between',
    gap: '10px',
  },
  assetContentContainer: {
    minWidth: '260px',
    maxWidth: '340px',
    alignSelf: 'flex-start',
    [theme.breakpoints.down('md')]: {
      width: 'auto',
      minWidth: 0,
      flexGrow: 1,
      alignSelf: 'auto',
      display: 'flex',
      flexWrap: 'wrap',
    },
  },
  assetTitleContainer: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    fontSize: '16px',
    color: '#000',

    [theme.breakpoints.down('md')]: {
      width: 'auto',
      flex: '1 1 100%',
      paddingRight: '5px',
      fontSize: '14px',
      WebkitLineClamp: 2,
    },
  },
  assetDateContainer: {
    width: '100%',
    fontSize: '14px',
    color: '#999',
    [theme.breakpoints.down('md')]: {
      width: 'auto',
      flex: '1 1 100%',
      paddingRight: '5px',
      fontSize: '11px',
    },
  },
  menuButton: {
    width: '100px',
    height: '36px',
    border: '1px solid #b6b6b6',
    borderRadius: 0,
    backgroundColor: 'transparent',
    fontSize: '14px',
    [theme.breakpoints.down('md')]: {
      flexBasis: '60px',
      flexShrink: 0,
      flexGrow: 0,
      fontSize: '14px',
    },
  },
  typeContainer: {
    width: '112px',
    height: '27px',
    position: 'absolute',
    padding: '2px',
    zIndex: 100,
    top: 0,
    right: 0,
    fontSize: '14px',
    textAlign: 'center',
    color: '#FFF',
    backgroundColor: '#D94F98',
    [theme.breakpoints.down('md')]: {
      width: 'auto',
      height: '20px',
      padding: '0 10px',
      fontSize: '12px',
    },
  },
  centerLine: {
    border: '1px solid gray',
    position: 'absolute',
    left: '50%',
    width: 0,
    height: '100%',
    display: 'inline-block',
  },
  teamContainer: {
    position: 'absolute',
    top: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    overflow: 'hidden',
  },
  teamLogoContainer: {
    width: '220px',
    height: '110px',
    position: 'relative',
    marginTop: '54px',
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 1.0)',
    [theme.breakpoints.down('md')]: {
      width: '171px',
      height: '85px',
      marginTop: '40px',
    },
  },
  teamBorder: {
    backgroundColor: '#000',
  },
  team: {
    position: 'relative',
    width: '50%',
    padding: '20px',
    '& > img': {
      width: '100%',
      height: '100%',
    },
  },
  leftBorder: {
    borderLeft: '0.5px solid gray',
  },
  verseTextContainer: {
    position: 'absolute',
    zIndex: 1,
    left: 'calc(50% - 15px)',
    width: '30px',
    lineHeight: 2.0,
    borderRadius: '50%',
    backgroundColor: 'rgba(202, 90, 150, 1.0)',
    color: 'rgba(255, 255, 255, 1.0)',
    fontWeight: 'bold',
    textAlign: 'center',
  },
  teamNameContainer: {
    width: '220px',
    height: '27px',
    display: 'flex',
    margin: 'auto',
    justifyContent: 'space-around',
    backgroundColor: '#999',
    '& > span': {
      padding: '2px',
      flexBasis: '50%',
      fontSize: '14px',
      color: 'rgba(255, 255, 255, 1.0)',
      textAlign: 'center',
      overflow: 'hidden',
    },
    [theme.breakpoints.down('md')]: {
      width: '171px',
      height: '20px',
      '& > span': {
        padding: 0,
        flexBasis: '50%',
        fontSize: '12px',
        color: 'rgba(255, 255, 255, 1.0)',
        textAlign: 'center',
        overflow: 'hidden',
      },
    },
  },
}));

const HtmlTooltip = withStyles((theme: Theme) => ({
  tooltip: {
    backgroundColor: '#FFF',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(14),
    border: '1px solid #dadde9',
  },
}))(Tooltip);

function Asset({ asset }: AssetProps) {
  const classes = useStyles();
  const { onRequestAssetDownloadCloudfront } = useAsset();
  const { onInsertLibrary } = useLibrary();

  const { categoryList } = useSelector((state: RootState) => state.category);
  const { storageList } = useSelector((state: RootState) => state.storage);
  const { wkblGameTypeList: gameTypeList, kfScriptRefRangeList: rangeCodeList } = useSelector(
    (state: RootState) => state.code,
  );

  const [skimPoisitionPercent, setSkimPositionPercent] = useState<number>(100);
  const [scrubbingPercent, setScrubbingPercent] = useState<number>(0);
  const [isShowScrubbingBar, setIsShowScrubbingBar] = useState<boolean>(false);
  const [isLoadingInsertLibrary, setIsLoadingInsertLibrary] = useState(false);

  const [inTimecodeUtil, setInTimecodeUtil] = useState<TimeCode>(() => {
    const timecodeUtil = new TimeCode(asset.mediaFps);
    timecodeUtil.setFrames(asset.inPoint);
    return timecodeUtil;
  });
  const [outTimecodeUtil, setOutTimecodeUtil] = useState<TimeCode>(() => {
    const timecodeUtil = new TimeCode(asset.mediaFps);
    timecodeUtil.setFrames(asset.outPoint);
    return timecodeUtil;
  });
  const [durationTimecodeUtil] = useState<TimeCode>(() => {
    const timecodeUtil = new TimeCode(asset.mediaFps);
    timecodeUtil.setFrames(asset.outPoint - asset.inPoint);
    return timecodeUtil;
  });

  const storage: StorageVO | null = useMemo(() => {
    let result = null;
    const category = categoryList.filter(({ id }: CategoryVO) => id === asset.categoryId)[0];
    if (category) {
      result = storageList.filter(({ id }: StorageVO) => id === category.storageId)[0];
    }
    return result;
  }, [categoryList, storageList, asset.categoryId]);

  const teamName = useMemo(() => {
    const { team1Name, team2Name } = asset;
    const team1 = team1Name ? team1Name.split(' ')[0] : '';
    const team2 = team2Name ? team2Name.split(' ')[0] : '';

    return [team1, team2];
  }, [asset.team1Name, asset.team2Name]);

  const isShowTeamContainer = useMemo(() => {
    return asset.team1 && asset.team2;
  }, [asset]);

  const { team1, team2 } = useMemo(() => {
    return asset.getTeamLogoPath();
  }, [asset]);

  const backgroundSkimImageStyle = useMemo(
    () => asset.getBackgroundImage(isShowScrubbingBar),
    [isShowScrubbingBar, asset],
  );

  const backgroundSkimPosition = useMemo(() => {
    return isShowScrubbingBar
      ? `0 ${skimPoisitionPercent}%, left bottom, left bottom`
      : 'left bottom, left bottom';
  }, [isShowScrubbingBar, skimPoisitionPercent]);

  const rangeCodeName = useMemo(() => {
    const rangeCode: CodeVO[] =
      rangeCodeList.filter((item: CodeVO) => item.codevalue === String(asset.subType)) || [];
    if (rangeCode.length > 0) {
      return rangeCode[0].name || '';
    }
    return '';
  }, [rangeCodeList, asset]);

  const onMouseMove = useCallback(event => {
    const mouseX = event.nativeEvent.x - event.currentTarget.getBoundingClientRect().left;

    const imageWidth = event.currentTarget.offsetWidth;
    const mousePercent: number = (mouseX / imageWidth) * 100.0;
    setScrubbingPercent(mousePercent);

    const integerPercent: number = parseInt(mousePercent.toFixed(0));

    if (integerPercent % 2 === 0) {
      setSkimPositionPercent(100 - integerPercent);
    }
  }, []);

  const onMouseEnterThumbnail = useCallback(() => {
    setIsShowScrubbingBar(true);
  }, []);

  const onMouseLeaveThumbnail = useCallback(() => {
    setIsShowScrubbingBar(false);
    setSkimPositionPercent(100);
  }, []);

  const onPopupAssetDetail = useCallback(
    (event: React.MouseEvent) => {
      const { refId, id, title, subType } = asset;
      const inTime = inTimecodeUtil.getSeconds();
      const outTime = outTimecodeUtil.getSeconds();
      const encodedKeyword = encodeURIComponent(subType);

      let redirectUrl = `/preview/${id}`;
      if (refId !== id && rangeCodeName) {
        redirectUrl = `/preview/${refId}?in=${inTime}&out=${outTime}&subType=${encodedKeyword}`;
      } else if (refId !== id && !rangeCodeName) {
        redirectUrl = `/preview/${refId}?in=${inTime}&out=${outTime}`;
      }
      window.open(redirectUrl);
    },
    [asset, inTimecodeUtil, outTimecodeUtil, rangeCodeName],
  );

  const onDownloadLowres = useCallback(() => {
    const { categoryId, lowresPath, id, title, formattedGameDate } = asset;
    const gameDate = formattedGameDate.replace(/-/g, '');
    onRequestAssetDownloadCloudfront({
      categoryId,
      fileName: lowresPath,
      downloadFileName: `${gameDate}_${title}.mp4`,
    });

    // TODO - lowresPath가 안나오는 경우도 있음(기존 서비스에서는 에러나지 않는 부분)
    // onRequestAssetDownloadCloudfront({
    //   categoryId,
    //   fileName: lowresPath`,
    // });
  }, []);

  return (
    <Grid id="assetThumbnailView" item xs={12} lg={4} key={asset.id} className={classes.assetItem}>
      <Paper elevation={2} className={clsx(classes.paper)}>
        <div
          className={clsx(classes.imgContainer)}
          style={{
            backgroundImage: backgroundSkimImageStyle,
            backgroundPosition: backgroundSkimPosition,
          }}
          onMouseMove={onMouseMove}
          onMouseEnter={onMouseEnterThumbnail}
          onMouseLeave={onMouseLeaveThumbnail}
          onClick={onPopupAssetDetail}
        >
          <div
            className={clsx(classes.scrubbingBar)}
            style={{
              left: `${scrubbingPercent}%`,
              display: isShowScrubbingBar ? 'inline-block' : 'none',
            }}
          />
          {isShowTeamContainer && (
            <div
              className={classes.teamContainer}
              style={{
                display: !isShowScrubbingBar ? 'inline-block' : 'none',
              }}
            >
              <div className={classes.teamLogoContainer}>
                {/* 홈 팀 로고 */}
                <span className={clsx(classes.team)}>
                  <img src={team1.db}></img>
                </span>
                <Divider className={classes.teamBorder} orientation="vertical" />
                <img className={classes.verseTextContainer} src={VersusIcon} />
                {/* 어웨이 팀 로고 */}
                <span className={classes.team}>
                  <img src={team2.db}></img>
                </span>
              </div>
              <div className={classes.teamNameContainer}>
                {/* 홈 팀 */}
                <span style={{ backgroundColor: asset.team1Color }}>{teamName[0]}</span>
                <Divider className={classes.teamBorder} orientation="vertical" />
                {/* 어웨이 팀 */}
                <span style={{ backgroundColor: asset.team2Color }}>{teamName[1]}</span>
              </div>
            </div>
          )}
          {rangeCodeName && (
            <Typography className={classes.typeContainer}>{rangeCodeName}</Typography>
          )}
        </div>
        <div className={clsx(classes.assetContainer)}>
          <div className={classes.assetContentContainer}>
            <HtmlTooltip
              title={
                <React.Fragment>
                  <Typography>{asset.title}</Typography>
                </React.Fragment>
              }
            >
              <div className={clsx(classes.assetTitleContainer)}>{asset.title}</div>
            </HtmlTooltip>
            <div className={clsx(classes.assetDateContainer)}>
              {asset.formattedGameDate} ({getDayOfWeek(asset.formattedGameDate)})
            </div>
          </div>
          <Hidden lgUp>
            <DownloadAssetSplitButton asset={asset} onDownloadClick={onDownloadLowres} />
          </Hidden>
          <Hidden mdDown>
            <DownloadAssetSplitButton asset={asset} onDownloadClick={onDownloadLowres} />
          </Hidden>
        </div>
      </Paper>
    </Grid>
  );
}

export default Asset;
